import { useRef, useState } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { PriceFormats } from "@repo/utils";
import { useEstimateStructure } from "@tools";
import { Dialog, InfoLabel, Radio } from "@components";
import { Grid, IconButton, Switch } from "@material-ui/core";
import { Settings } from "@assets";
import classnames from "classnames";

export const HeaderPriceActions = observer(( {
  column,
  absolute,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const {
    timeModifier,
    roundPrice,
    priceFormat,
    showPriceFromHiddenWorkTypes,
  } = structure.settings;

  const [visible, show] = useState(false);

  const anchor = useRef(null);
  
  const handleVisibility = () => show(false);
  
  const handlePriceFormatChange = (e) => {
    structure.settings.setPriceFormat(Number(e.target.value));
  };
  
  const handlePriceRoundingChange = () => {
    structure.settings.setPriceRounding(!roundPrice);
  };
  
  const handlePinPriceColumn = () => {
    column.setApplyPin(!column.applyPin);
  };
  
  const handleShowPriceFromHiddenWorkTypes = () => {
    structure.settings.setShowPriceFromHiddenWorkTypes(!showPriceFromHiddenWorkTypes);
  };

  return (
    <>
      <IconButton
        style={absolute ? { right: -8 } : undefined}
        size="small"
        color="primary"
        onClick={() => show(!visible)}
        ref={anchor}
        tabIndex={-1}
        aria-label="price settings"
        className={ classnames("noDrag cellHoverIcon text-sm", absolute && "absolute") }
      >
        <Settings />
      </IconButton>

      <Dialog
        open={visible}
        onClose={handleVisibility}
        width={460}
        title={t("views.editor.dialogs.price_settings.title")}
      >
        <Grid item container direction="column" wrap="nowrap" spacing={1} className="mb-4">
          <InfoLabel
            className="my-0"
            label="views.editor.dialogs.price_settings.format"
          />
          <Grid item xs={ 12 }>
            <Radio
              name="price-format"
              aria-label="price-format"
              value={PriceFormats.EUROPEAN}
              checked={priceFormat === PriceFormats.EUROPEAN}
              onChange={handlePriceFormatChange}
              className="mr-2"
              labelClassName="m-0"
              label={<>
                <span>{t("views.editor.dialogs.price_settings.formats.european")}</span>
                <span className="color-section ml-1">
                  {t("views.editor.dialogs.price_settings.formats.european_eg")}
                </span>
              </>}
            />
          </Grid>
          <Grid item xs={ 12 }>
            <Radio
              name="price-format"
              aria-label="price-format"
              value={PriceFormats.AMERICAN}
              checked={priceFormat === PriceFormats.AMERICAN}
              onChange={handlePriceFormatChange}
              className="mr-2"
              labelClassName="m-0"
              label={<>
                <span>{t("views.editor.dialogs.price_settings.formats.american")}</span>
                <span className="color-section ml-1">
                  {t("views.editor.dialogs.price_settings.formats.american_eg")}
                </span>
              </>}
            />
          </Grid>
        </Grid>
        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          wrap="nowrap"
          className="mb-4"
        >
          <InfoLabel
            className="my-0"
            label="views.editor.dialogs.price_settings.rounding_price"
            tooltipText={`views.editor.dialogs.price_settings.tooltips.round_price${
              timeModifier === 1 ? "" : "_day"
            }`}
          />
          <Switch
            size="medium"
            color="primary"
            checked={roundPrice}
            onChange={handlePriceRoundingChange}
            name={t(
              `views.editor.dialogs.price_settings.${
                roundPrice ? "no_price_rounding" : "round_price"
              }`
            )}
          />
        </Grid>
        {
          Boolean(column) &&
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
            className="mb-4"
          >
            <InfoLabel
              className="my-0"
              label="views.editor.dialogs.price_settings.pin.label"
              tooltipText={`views.editor.dialogs.price_settings.pin.tooltip`}
            />
            <Switch
              size="medium"
              color="primary"
              checked={column.applyPin}
              onChange={handlePinPriceColumn}
              name={t(
                `views.editor.dialogs.price_settings.${column.applyPin ? "off" : "on"}`
              )}
            />
          </Grid>
        }
        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          wrap="nowrap"
          className="mb-4"
        >
          <InfoLabel
            className="my-0"
            label="views.editor.dialogs.price_settings.hidden_work_types.label"
          />
          <Switch
            size="medium"
            color="primary"
            checked={showPriceFromHiddenWorkTypes}
            onChange={handleShowPriceFromHiddenWorkTypes}
            name={t(
              `views.editor.dialogs.hidden_work_types.${showPriceFromHiddenWorkTypes ? "off" : "on"}`
            )}
          />
        </Grid>
      </Dialog>
    </>
  );
});

HeaderPriceActions.propTypes = {
  column: object,
  absolute: bool,
};
